import { useCallback, useEffect, useState } from 'react';
import { adminStats } from '@suangua/shared';
import { LoadingRow, PageHeader, useAdminGuard } from '../parts';

/**
 * 管理端 · 统计页：注册用户 / 帖子总数 / 待处理举报 / 占卜灵验分布。
 */
export function StatsPage() {
  const guard = useAdminGuard();
  const [stats, setStats] = useState<Awaited<ReturnType<typeof adminStats>> | null>(null);
  const [error, setError] = useState('');
  const [loadedAt, setLoadedAt] = useState('');

  const load = useCallback(() => {
    setError('');
    return adminStats()
      .then((s) => {
        setStats(s);
        setLoadedAt(new Date().toLocaleTimeString());
      })
      .catch((e) => setError(guard(e)));
  }, [guard]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-600">{error}</p>;
  if (!stats) return <LoadingRow />;

  const verdictLabels: Record<string, string> = { accurate: '灵', inaccurate: '不灵', uncertain: '不好说' };
  const verdictTotal = Object.values(stats.verdicts).reduce((a, b) => a + b, 0);

  return (
    <div className="max-w-3xl">
      <PageHeader title="数据统计" desc={`更新于 ${loadedAt || '—'}`}>
        <button
          onClick={() => void load()}
          className="rounded-lg border border-stone-200 bg-white px-3 py-1.5 text-xs text-stone-600 transition-colors hover:bg-stone-50"
        >
          <i className="fa-solid fa-rotate-right mr-1 text-stone-400" /> 刷新
        </button>
      </PageHeader>

      <div className="grid grid-cols-3 gap-4">
        <StatCard icon="fa-solid fa-users" label="注册用户" value={stats.users} />
        <StatCard icon="fa-regular fa-file-lines" label="帖子总数" value={stats.posts} />
        <StatCard
          icon="fa-regular fa-flag"
          label="待处理举报"
          value={stats.pendingReports}
          highlight={stats.pendingReports > 0}
        />
      </div>

      {/* 占卜灵验分布 */}
      <div className="mt-4 rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
        <p className="text-sm font-medium text-stone-700">占卜灵验分布（占卜板块标记）</p>
        {verdictTotal === 0 ? (
          <p className="mt-3 text-xs text-stone-400">暂无灵验标记数据</p>
        ) : (
          <div className="mt-3 space-y-2.5">
            {Object.entries(stats.verdicts).map(([k, v]) => {
              const pct = Math.round((v / verdictTotal) * 100);
              const color = { accurate: 'bg-green-500', inaccurate: 'bg-stone-400', uncertain: 'bg-amber-500' }[k] ?? 'bg-stone-300';
              return (
                <div key={k} className="flex items-center gap-3 text-xs">
                  <span className="w-12 shrink-0 text-stone-500">{verdictLabels[k] ?? k}</span>
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-stone-100">
                    <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
                  </div>
                  <span className="w-16 shrink-0 text-right text-stone-500">
                    {v} · {pct}%
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard(props: { icon: string; label: string; value: number | string; highlight?: boolean }) {
  return (
    <div
      className={`rounded-2xl border bg-white p-4 shadow-sm ${
        props.highlight ? 'border-red-200' : 'border-stone-200'
      }`}
    >
      <p className="flex items-center gap-1.5 text-xs text-stone-400">
        <i className={props.icon} />
        {props.label}
      </p>
      <p className={`mt-1 text-2xl font-bold ${props.highlight ? 'text-red-600' : 'text-stone-800'}`}>{props.value}</p>
    </div>
  );
}
